import { fetchProfile, patchProfile } from '../api/profileApi';
import { getStoredLoginIdentifier } from '../auth/sessionToken';
import { extractProfileDisplaySummary } from './profileDisplaySummary';
import { buildProfileEditPatchBody } from './profileEditSave';
import { prepareOwnerLogoDataUrlForUpload } from './profileOwnerLogoUpload';
import { recordProfileUpdatedFromPatchResponse } from './profileLastUpdated';

/** Keep JSON PATCH bodies under typical ~100 KB API limits (field name + JSON overhead). */
export const PROFILE_AVATAR_UPLOAD_MAX_BYTES = 70_000;

export function estimateDataUrlBytes(dataUrl: string): number {
  const comma = dataUrl.indexOf(',');
  if (comma < 0) {
    return dataUrl.length;
  }
  const base64 = dataUrl.slice(comma + 1);
  return Math.ceil((base64.length * 3) / 4);
}

async function loadImageFromDataUrl(dataUrl: string): Promise<HTMLImageElement> {
  const image = new Image();
  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = () => reject(new Error('Could not load profile image'));
    image.src = dataUrl;
  });
  return image;
}

function canvasToJpegDataUrl(canvas: HTMLCanvasElement, quality: number): string | null {
  return canvas.toDataURL('image/jpeg', quality);
}

export async function compressProfileAvatarDataUrlForUpload(
  dataUrl: string,
  maxBytes: number = PROFILE_AVATAR_UPLOAD_MAX_BYTES,
): Promise<string> {
  if (!dataUrl.startsWith('data:image/')) {
    return dataUrl;
  }
  if (estimateDataUrlBytes(dataUrl) <= maxBytes) {
    return dataUrl;
  }

  const image = await loadImageFromDataUrl(dataUrl);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return dataUrl;
  }

  let edge = Math.min(512, Math.max(image.width, image.height));
  let best = dataUrl;

  try {
    for (let shrink = 0; shrink < 8; shrink += 1) {
      const scale = edge / Math.max(image.width, image.height);
      const width = Math.max(1, Math.round(image.width * scale));
      const height = Math.max(1, Math.round(image.height * scale));
      canvas.width = width;
      canvas.height = height;
      ctx.clearRect(0, 0, width, height);
      ctx.drawImage(image, 0, 0, width, height);

      for (let quality = 0.88; quality >= 0.45; quality -= 0.06) {
        const candidate = canvasToJpegDataUrl(canvas, quality);
        if (!candidate) {
          continue;
        }
        if (estimateDataUrlBytes(candidate) <= maxBytes) {
          return candidate;
        }
        best = candidate;
      }

      edge = Math.max(96, Math.round(edge * 0.82));
    }
  } catch {
    return dataUrl;
  }

  return best;
}

/** Download a remote avatar (e.g. Google profile photo) for PATCH /profile upload. */
export async function fetchRemoteImageAsUploadDataUrl(url: string): Promise<string | null> {
  const trimmed = url.trim();
  if (!trimmed || typeof fetch === 'undefined') {
    return null;
  }
  try {
    const response = await fetch(trimmed);
    if (!response.ok) {
      return null;
    }
    const blob = await response.blob();
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          resolve(reader.result);
          return;
        }
        reject(new Error('Could not read remote image'));
      };
      reader.onerror = () => reject(new Error('Could not read remote image'));
      reader.readAsDataURL(blob);
    });
    return compressProfileAvatarDataUrlForUpload(dataUrl);
  } catch {
    return null;
  }
}

export function extractProfileAvatarFromResponse(data: unknown): string | null {
  return extractProfileDisplaySummary(data, null, null).avatarSrc;
}

/** Build PATCH /profile body for name, occupation, birth date, optional avatar, optional owner logo. */
export async function buildProfileEditPatchPayload(
  firstName: string,
  lastName: string,
  occupation: string,
  birthDate: string,
  avatarDataUrl?: string | null,
  ownerLogoDataUrl?: string | null,
  options?: { email?: string; phoneNumber?: string },
): Promise<Record<string, unknown>> {
  let profileImage: string | undefined;
  if (avatarDataUrl?.startsWith('data:image/')) {
    profileImage = await compressProfileAvatarDataUrlForUpload(avatarDataUrl);
  }
  let logo: string | undefined;
  if (ownerLogoDataUrl?.startsWith('data:image/')) {
    logo = await prepareOwnerLogoDataUrlForUpload(ownerLogoDataUrl);
  }
  return buildProfileEditPatchBody({
    firstName,
    lastName,
    occupation,
    birthDate,
    profileImage,
    logo,
    email: options?.email,
    phoneNumber: options?.phoneNumber,
  });
}

/** Upload avatar to the profile API and return the remote profile URL after PATCH. */
export async function uploadProfileAvatarToServer(dataUrl: string): Promise<string | null> {
  const uploadDataUrl = await compressProfileAvatarDataUrlForUpload(dataUrl);

  const response = await patchProfile({ profile_image: uploadDataUrl });
  recordProfileUpdatedFromPatchResponse(response, getStoredLoginIdentifier());

  try {
    const afterSrc = extractProfileAvatarFromResponse(await fetchProfile());
    if (!afterSrc || afterSrc.startsWith('data:')) {
      return null;
    }
    if (/^https?:\/\//i.test(afterSrc)) {
      return afterSrc;
    }
    if (afterSrc.startsWith('/') && !afterSrc.startsWith('data:')) {
      return afterSrc;
    }
  } catch {
    /* keep local crop if refresh fails */
  }

  return null;
}
